import * as React from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

const textareaVariants = cva(
  cn(
    'flex min-h-[60px] w-full py-3 text-sm shadow-none text-foreground lg:text-base',
    'outline-none focus:outline-none focus-visible:outline-none',
    'ring-0 focus:ring-0 focus-visible:ring-0 focus-within:ring-0',
    'ring-offset-0 focus:ring-offset-0 focus-visible:ring-offset-0',
    'focus:border-inherit focus-visible:border-inherit',
    'placeholder:text-[#636264]',
  ),
  {
    variants: {
      variant: {
        transparent: ['bg-transparent', 'ea-textarea__transparent'],
        filled: ['bg-white', 'ea-textarea__filled'],
      },
      rounded: {
        default: 'rounded-lg',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  VariantProps<typeof textareaVariants> & {
    className?: string;
    classNames?: string;
    bottomBorder?: boolean;
  };

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, classNames, bottomBorder, ...props }, ref) => {
    return (
      <textarea
        style={{ resize: 'none' }}
        className={cn(
          textareaVariants({
            variant: props.variant,
            rounded: props.rounded,
            className: cn(
              'ea-textarea min-h-[104px] text-foreground/80',
              props['aria-invalid'] && 'ea-textarea--invalid border-destructive text-destructive',
              className,
              classNames,
              !bottomBorder && 'px-4',
              !!bottomBorder && 'border-b border-b-[#D3C7B8]',
            ),
          }),
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
